import React, { useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, CardHeader, 
  Divider, TextField, Button, Switch, FormControlLabel, Alert,
  Snackbar, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { firestore } from '../services/firebase';

export default function Settings() {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [loading, setLoading] = useState(false);
  
  // App settings
  const [appSettings, setAppSettings] = useState({
    appName: 'Cargo Tour',
    supportEmail: 'support@example.com',
    notificationsEnabled: true,
    dataRetentionDays: 90
  });
  
  // Firebase settings (these would typically be stored securely, not in client-side code)
  const [firebaseSettings, setFirebaseSettings] = useState({
    projectId: '',
    apiKey: '',
    databaseUrl: '',
    storageBucket: ''
  });

  const handleAppSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setAppSettings({
      ...appSettings,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleFirebaseSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFirebaseSettings({
      ...firebaseSettings,
      [name]: value
    });
  };

  const saveAppSettings = async () => {
    try {
      setLoading(true);
      
      // In a real app, you would save these settings to your Firebase database
      await setDoc(doc(firestore, 'settings', 'appSettings'), appSettings);
      
      setSnackbar({
        open: true,
        message: 'App settings saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving app settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save app settings',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const saveFirebaseSettings = async () => {
    try {
      setLoading(true);
      
      // In a real app, these would be stored securely, not in client-side accessible Firestore
      // This is for demo purposes only
      await setDoc(doc(firestore, 'settings', 'firebaseSettings'), firebaseSettings);
      
      setSnackbar({
        open: true,
        message: 'Firebase settings saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving Firebase settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save Firebase settings',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Settings
      </Typography>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardHeader title="App Settings" />
            <Divider />
            <CardContent>
              <Box component="form" noValidate autoComplete="off">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="App Name"
                      name="appName"
                      value={appSettings.appName}
                      onChange={handleAppSettingsChange}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Support Email"
                      name="supportEmail"
                      value={appSettings.supportEmail}
                      onChange={handleAppSettingsChange}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Data Retention (days)"
                      name="dataRetentionDays"
                      type="number"
                      value={appSettings.dataRetentionDays}
                      onChange={handleAppSettingsChange}
                      margin="normal"
                      inputProps={{ min: 1 }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={appSettings.notificationsEnabled}
                          onChange={handleAppSettingsChange}
                          name="notificationsEnabled"
                          color="primary"
                        />
                      }
                      label="Enable Push Notifications"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={saveAppSettings}
                      disabled={loading}
                    >
                      Save App Settings
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card elevation={3}>
            <CardHeader 
              title="Firebase Configuration" 
              subheader="Configure connection to your app databases"
            />
            <Divider />
            <CardContent>
              <Alert severity="info" sx={{ mb: 2 }}>
                These settings should be kept secure. In a production environment, 
                these would typically be stored in server-side environment variables.
              </Alert>
              <Box component="form" noValidate autoComplete="off">
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Firebase Project ID"
                      name="projectId"
                      value={firebaseSettings.projectId}
                      onChange={handleFirebaseSettingsChange}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="API Key"
                      name="apiKey"
                      value={firebaseSettings.apiKey}
                      onChange={handleFirebaseSettingsChange}
                      margin="normal"
                      type="password"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Database URL"
                      name="databaseUrl"
                      value={firebaseSettings.databaseUrl}
                      onChange={handleFirebaseSettingsChange}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Storage Bucket"
                      name="storageBucket"
                      value={firebaseSettings.storageBucket}
                      onChange={handleFirebaseSettingsChange}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={saveFirebaseSettings}
                      disabled={loading}
                    >
                      Save Firebase Settings
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12}>
          <Card elevation={3}>
            <CardHeader title="Advanced Settings" />
            <Divider />
            <CardContent>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>Data Management</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph>
                    Configure settings related to data management, backups, and exports.
                    These features would typically be implemented based on specific requirements.
                  </Typography>
                  <Button variant="outlined" color="primary" disabled>
                    Export All Data
                  </Button>
                </AccordionDetails>
              </Accordion>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>User Permissions</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph>
                    Configure admin user roles and permissions.
                    This section would allow you to manage access control for different administrators.
                  </Typography>
                  <Button variant="outlined" color="primary" disabled>
                    Manage Admin Users
                  </Button>
                </AccordionDetails>
              </Accordion>
              
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography>API Access</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography paragraph>
                    Configure API access tokens and permissions for external integrations.
                  </Typography>
                  <Button variant="outlined" color="primary" disabled>
                    Generate API Key
                  </Button>
                </AccordionDetails>
              </Accordion>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
